import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type { AccountCreationFormData } from '../../types/applicationForm';
import { EMPTY_ACCOUNT_CREATION_FORM } from '../../types/applicationForm';
import { useAccountCreationFieldOptions } from '../../lib/accountCreationFieldOptions';
import type { ApplicationSubmissionSource } from '../../types/applicationForm';
import {
  normalizeAcraAttachments,
  normalizeSupportingAttachments,
  type AccountCreationPendingFiles,
  uploadAcraAttachment,
  validateAcraAttachment,
} from '../../lib/applicationFormSubmissions';
import { emailValidationMessage } from '../../lib/emailValidation';
import { SignaturePad } from './SignaturePad';
import { ACCOUNT_CREATION_DECLARATION_TEXT } from '../../lib/accountCreationDeclaration';
import { ErrorOverlay } from '../ui/ErrorOverlay';

type Props = {
  initialCompanyName?: string;
  initialFormData?: Partial<AccountCreationFormData>;
  submitLabel?: string;
  busy?: boolean;
  /** Public customer submissions require declaration; internal staff submissions do not. */
  requireDeclaration?: boolean;
  /** Internal submissions require salesperson code before submit. */
  requireSalespersonCode?: boolean;
  /** When true, hide the submit button (e.g. embedded in verify flow). */
  hideSubmit?: boolean;
  /** When true, all fields are non-editable (verifier preview until Edit). */
  readOnly?: boolean;
  /**
   * AR pending-approval default: finance contact name / no / email stay editable
   * even while the rest of the form is read-only.
   */
  financeContactEditable?: boolean;
  /** AR pending-approval: finance contact name / no / email are mandatory. */
  requireFinanceContact?: boolean;
  /**
   * When set (detail/verify edit), files upload immediately into form_data.
   * New applications omit this and upload on submit instead.
   */
  uploadSource?: ApplicationSubmissionSource;
  onValuesChange?: (companyName: string, formData: AccountCreationFormData) => void;
  onSubmit: (
    companyName: string,
    formData: AccountCreationFormData,
    pendingFiles?: AccountCreationPendingFiles,
  ) => Promise<void>;
};

function Field({
  label,
  required,
  children,
  full,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`af-form__field${full ? ' af-form__field--full' : ''}`}>
      <span>
        {label}
        {required ? <span className="af-form__req">*</span> : null}
      </span>
      {children}
    </label>
  );
}

function todayIsoDate(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function PendingOrSavedFiles({
  pending,
  saved,
  locked,
  onRemovePending,
}: {
  pending: File[];
  saved: { path: string; name: string }[];
  locked: boolean;
  onRemovePending: (index: number) => void;
}) {
  if (pending.length === 0 && saved.length === 0) return null;
  return (
    <ul className="af-form__pending-files">
      {saved.map((file) => (
        <li key={`saved-${file.path}`}>
          <span>{file.name}</span>
        </li>
      ))}
      {pending.map((file, index) => (
        <li key={`pending-${file.name}-${index}`}>
          <span>{file.name}</span>
          {!locked ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => onRemovePending(index)}
            >
              Remove
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function AccountCreationForm({
  initialCompanyName = '',
  initialFormData,
  submitLabel = 'Submit Application',
  busy = false,
  requireDeclaration = true,
  requireSalespersonCode = false,
  hideSubmit = false,
  readOnly = false,
  financeContactEditable = false,
  requireFinanceContact = false,
  uploadSource,
  onValuesChange,
  onSubmit,
}: Props) {
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [formData, setFormData] = useState<AccountCreationFormData>({
    ...EMPTY_ACCOUNT_CREATION_FORM,
    signatory_date: todayIsoDate(),
    ...initialFormData,
    acra_attachments: normalizeAcraAttachments({
      ...EMPTY_ACCOUNT_CREATION_FORM,
      ...initialFormData,
    }),
    supporting_attachments: normalizeSupportingAttachments({
      ...EMPTY_ACCOUNT_CREATION_FORM,
      ...initialFormData,
    }),
  });
  const [pendingAcraFiles, setPendingAcraFiles] = useState<File[]>([]);
  const [pendingSupportingFiles, setPendingSupportingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { options: fieldOptions } = useAccountCreationFieldOptions();

  useEffect(() => {
    onValuesChange?.(companyName, formData);
  }, [companyName, formData, onValuesChange]);

  const patch = <K extends keyof AccountCreationFormData>(key: K, value: AccountCreationFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const appendPending = async (kind: 'acra' | 'supporting', list: FileList | null) => {
    if (!list?.length || locked) return;
    const next: File[] = [];
    for (const file of Array.from(list)) {
      const issue = validateAcraAttachment(file);
      if (issue) {
        setError(issue);
        return;
      }
      next.push(file);
    }
    setError(null);

    if (!uploadSource) {
      if (kind === 'acra') setPendingAcraFiles((prev) => [...prev, ...next]);
      else setPendingSupportingFiles((prev) => [...prev, ...next]);
      return;
    }

    try {
      const uploaded: { path: string; name: string }[] = [];
      for (const file of next) {
        uploaded.push(await uploadAcraAttachment(file, uploadSource, kind));
      }
      setFormData((prev) => {
        if (kind === 'acra') {
          const merged = [...normalizeAcraAttachments(prev), ...uploaded];
          return {
            ...prev,
            acra_attachments: merged,
            acra_attachment_path: merged[0]?.path ?? '',
            acra_attachment_name: merged[0]?.name ?? '',
          };
        }
        return {
          ...prev,
          supporting_attachments: [...normalizeSupportingAttachments(prev), ...uploaded],
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!companyName.trim()) {
      setError('Registered company name is required.');
      return;
    }
    if (!formData.outlet_name.trim()) {
      setError('Outlet Name is required.');
      return;
    }
    // Internal: finance contact fields are optional. Public (declaration) still requires them.
    if (requireDeclaration) {
      if (
        !formData.finance_contact_name.trim() ||
        !formData.finance_contact_no.trim() ||
        !formData.finance_email.trim()
      ) {
        setError('Finance contact name, number and email are required.');
        return;
      }
    }
    if (formData.finance_email.trim()) {
      const financeEmailError = emailValidationMessage('Finance email', formData.finance_email);
      if (financeEmailError) {
        setError(financeEmailError);
        return;
      }
    }
    if (!formData.sales_contact_name.trim() || !formData.sales_contact_no.trim() || !formData.sales_email.trim()) {
      setError('Purchaser contact name, number and email are required.');
      return;
    }
    const purchaserEmailError = emailValidationMessage('Purchaser email', formData.sales_email);
    if (purchaserEmailError) {
      setError(purchaserEmailError);
      return;
    }
    if (requireSalespersonCode && !formData.salesperson_code.trim()) {
      setError('Salesperson code is required for internal submissions.');
      return;
    }
    if (requireDeclaration) {
      if (!formData.declaration_accepted) {
        setError('Please accept the declaration before submitting.');
        return;
      }
      if (!formData.signatory_name.trim()) {
        setError('Signatory name is required.');
        return;
      }
      if (!formData.signatory_title.trim()) {
        setError('Signatory position / title is required.');
        return;
      }
      if (!formData.signature_data_url.trim()) {
        setError('Signature is required.');
        return;
      }
    }
    try {
      const payload: AccountCreationFormData = requireDeclaration
        ? {
            ...formData,
            salesperson_code: formData.salesperson_code.trim(),
            signatory_date: formData.signatory_date || todayIsoDate(),
          }
        : {
            ...formData,
            declaration_accepted: false,
            signatory_name: '',
            signatory_title: '',
            signatory_date: '',
            signature_data_url: '',
            salesperson_code: formData.salesperson_code.trim(),
          };
      await onSubmit(companyName.trim(), payload, {
        acra: pendingAcraFiles,
        supporting: pendingSupportingFiles,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed.');
    }
  };

  const financeRequired = requireDeclaration || requireFinanceContact;

  const locked = readOnly || busy;
  const financeLocked = busy || (readOnly && !financeContactEditable);
  const arPartial = Boolean(readOnly && financeContactEditable);

  return (
    <form
      className={`af-form${readOnly ? ' af-form--readonly' : ''}${arPartial ? ' af-form--ar-partial' : ''}`}
      onSubmit={handleSubmit}
    >
      {error ? (
        <ErrorOverlay message={error} onClose={() => setError(null)} />
      ) : null}

      <fieldset className="af-form__fieldset" disabled={locked}>
      <section className="af-form__section">
        <h2 className="af-form__section-title">Application type</h2>
        <div
          className={`af-form__grid${requireSalespersonCode ? ' af-form__grid--cols-3' : ' af-form__grid--cols-2'}`}
        >
          <Field label="Apply for" required>
            <select
              value={formData.apply_for}
              onChange={(e) => patch('apply_for', e.target.value as AccountCreationFormData['apply_for'])}
              required
            >
              {fieldOptions.apply_for.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Billing &amp; SOA" required>
            <select
              value={formData.billing_soa}
              onChange={(e) => patch('billing_soa', e.target.value as AccountCreationFormData['billing_soa'])}
              required
            >
              {fieldOptions.billing_soa.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          {requireSalespersonCode ? (
            <Field label="Salesperson code" required>
              <select
                value={formData.salesperson_code}
                onChange={(e) => patch('salesperson_code', e.target.value)}
                required
              >
                <option value="">Select salesperson</option>
                {fieldOptions.salesperson_code.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
      </section>

      <section className="af-form__section">
        <h2 className="af-form__section-title">Customer general information</h2>
        <div className="af-form__grid">
          <Field label="Registered Company Name" required full>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </Field>
        </div>
        <div className="af-form__grid af-form__grid--cols-2" style={{ marginTop: '0.75rem' }}>
          <Field label="Company Reg. No.">
            <input
              type="text"
              value={formData.company_reg_no}
              onChange={(e) => patch('company_reg_no', e.target.value)}
            />
          </Field>
          <Field label="GST Reg. No.">
            <input
              type="text"
              value={formData.gst_reg_no}
              onChange={(e) => patch('gst_reg_no', e.target.value)}
            />
          </Field>
        </div>
        <p className="af-form__hint" style={{ marginTop: '0.65rem' }}>
          Credit application form and ACRA business profile is required for 30 days payment terms.
        </p>
      </section>

      <section className="af-form__section">
        <h2 className="af-form__section-title">Attachments</h2>
        <div className="af-form__section-body">
          <div className="af-form__grid af-form__grid--cols-2">
            <Field label="ACRA Business Profile">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                disabled={locked}
                onChange={(e) => {
                  void appendPending('acra', e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="af-form__hint">PDF or image · unlimited · max 10 MB each</p>
              <PendingOrSavedFiles
                pending={pendingAcraFiles}
                saved={normalizeAcraAttachments(formData)}
                locked={locked}
                onRemovePending={(index) =>
                  setPendingAcraFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            </Field>
            <Field label="Supporting Documents">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                disabled={locked}
                onChange={(e) => {
                  void appendPending('supporting', e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="af-form__hint">PDF or image · unlimited · max 10 MB each</p>
              <PendingOrSavedFiles
                pending={pendingSupportingFiles}
                saved={normalizeSupportingAttachments(formData)}
                locked={locked}
                onRemovePending={(index) =>
                  setPendingSupportingFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="af-form__section">
        <h2 className="af-form__section-title">Billing Address</h2>
        <div className="af-form__grid af-form__grid--cols-2">
          <Field label="Street name" required>
            <input
              type="text"
              value={formData.billing_street}
              onChange={(e) => patch('billing_street', e.target.value)}
              required
            />
          </Field>
          <Field label="Building / Food court name">
            <input
              type="text"
              value={formData.billing_building}
              onChange={(e) => patch('billing_building', e.target.value)}
            />
          </Field>
          <Field label="Unit no.">
            <input
              type="text"
              value={formData.billing_unit}
              onChange={(e) => patch('billing_unit', e.target.value)}
            />
          </Field>
          <Field label="Postal code" required>
            <input
              type="text"
              value={formData.billing_postal}
              onChange={(e) => patch('billing_postal', e.target.value)}
              required
            />
          </Field>
        </div>
      </section>

      <section className="af-form__section">
        <h2 className="af-form__section-title">Delivery Address</h2>
        <div className="af-form__grid">
          <Field label="Outlet name" required full>
            <input
              type="text"
              value={formData.outlet_name}
              onChange={(e) => patch('outlet_name', e.target.value)}
              required
            />
          </Field>
        </div>
        <div className="af-form__grid af-form__grid--cols-2" style={{ marginTop: '0.75rem' }}>
          <Field label="Street name" required>
            <input
              type="text"
              value={formData.delivery_street}
              onChange={(e) => patch('delivery_street', e.target.value)}
              required
            />
          </Field>
          <Field label="Building / Food court name">
            <input
              type="text"
              value={formData.delivery_building}
              onChange={(e) => patch('delivery_building', e.target.value)}
            />
          </Field>
          <Field label="Unit no.">
            <input
              type="text"
              value={formData.delivery_unit}
              onChange={(e) => patch('delivery_unit', e.target.value)}
            />
          </Field>
          <Field label="Postal code" required>
            <input
              type="text"
              value={formData.delivery_postal}
              onChange={(e) => patch('delivery_postal', e.target.value)}
              required
            />
          </Field>
        </div>
      </section>

      </fieldset>

      <div className="af-form__contacts">
        <section
          className={`af-form__section${arPartial ? ' af-form__section--finance-editable' : ''}`}
        >
          <h2 className="af-form__section-title">Finance</h2>
          <div className="af-form__section-body">
            <fieldset className="af-form__fieldset" disabled={financeLocked}>
              <div className="af-form__grid">
                <Field label="Contact name" required={financeRequired} full>
                  <input
                    type="text"
                    value={formData.finance_contact_name}
                    onChange={(e) => patch('finance_contact_name', e.target.value)}
                    required={financeRequired}
                  />
                </Field>
                <Field label="Contact no." required={financeRequired} full>
                  <input
                    type="text"
                    value={formData.finance_contact_no}
                    onChange={(e) => patch('finance_contact_no', e.target.value)}
                    required={financeRequired}
                  />
                </Field>
                <Field label="Email" required={financeRequired} full>
                  <input
                    type="email"
                    value={formData.finance_email}
                    onChange={(e) => patch('finance_email', e.target.value)}
                    required={financeRequired}
                  />
                </Field>
              </div>
            </fieldset>
          </div>
        </section>
        <section className="af-form__section">
          <h2 className="af-form__section-title">Purchaser</h2>
          <div className="af-form__section-body">
            <fieldset className="af-form__fieldset" disabled={locked}>
              <div className="af-form__grid">
                <Field label="Contact name" required full>
                  <input
                    type="text"
                    value={formData.sales_contact_name}
                    onChange={(e) => patch('sales_contact_name', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Contact no." required full>
                  <input
                    type="text"
                    value={formData.sales_contact_no}
                    onChange={(e) => patch('sales_contact_no', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Email" required full>
                  <input
                    type="email"
                    value={formData.sales_email}
                    onChange={(e) => patch('sales_email', e.target.value)}
                    required
                  />
                </Field>
              </div>
            </fieldset>
          </div>
        </section>
      </div>

      <fieldset className="af-form__fieldset" disabled={locked}>
      {requireDeclaration ? (
        <section className="af-form__section">
          <h2 className="af-form__section-title">Declaration</h2>
          <p className="af-form__note">{ACCOUNT_CREATION_DECLARATION_TEXT}</p>
          <label className="af-form__checkbox">
            <input
              type="checkbox"
              checked={formData.declaration_accepted}
              onChange={(e) => patch('declaration_accepted', e.target.checked)}
            />
            <span>
              I confirm that I have read and agree to the declaration above{' '}
              <span className="af-form__req">*</span>
            </span>
          </label>
          <div className="af-form__grid af-form__grid--cols-2" style={{ marginTop: '0.75rem' }}>
            <Field label="Authorised signatory Name" required>
              <input
                type="text"
                value={formData.signatory_name}
                onChange={(e) => patch('signatory_name', e.target.value)}
                autoComplete="name"
              />
            </Field>
            <Field label="Position / Title" required>
              <input
                type="text"
                value={formData.signatory_title}
                onChange={(e) => patch('signatory_title', e.target.value)}
              />
            </Field>
            <div className="af-form__field">
              <span>Date</span>
              <p className="af-form__static-value">{formData.signatory_date || todayIsoDate()}</p>
            </div>
            <div className="af-form__field af-form__field--full">
              <span>
                Signature <span className="af-form__req">*</span>
              </span>
              <SignaturePad
                value={formData.signature_data_url}
                onChange={(dataUrl) => patch('signature_data_url', dataUrl)}
                disabled={busy}
              />
            </div>
          </div>
        </section>
      ) : (
        <section className="af-form__section">
          <h2 className="af-form__section-title">Declaration</h2>
          <p className="af-form__hint">
            Not applicable — internal submission. Customer declaration must be obtained via the
            public application form.
          </p>
        </section>
      )}

      </fieldset>

      {hideSubmit ? null : (
        <div className="af-form__actions">
          <button type="submit" className="btn-primary" disabled={busy || readOnly}>
            {busy ? 'Submitting…' : submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
